import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Eye, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

export default function AgentLLCFormations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [llcFormations, setLlcFormations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchLLCFormations();
    }
  }, [user]);

  const fetchLLCFormations = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/agent/llc-formations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLlcFormations(response.data);
    } catch (error) {
      console.error('Error fetching LLC formations:', error);
      toast.error('Failed to load LLC formations');
    } finally {
      setLoading(false);
    }
  };

  const filteredFormations = llcFormations.filter(llc =>
    llc.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    llc.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="LLC Formations" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="LLC Formations" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'Agent', href: '/agent/dashboard' },
          { label: 'LLC Formations' }
        ]} />

        <div>
          <h2 className="text-2xl font-bold">LLC Formations</h2>
          <p className="text-muted-foreground">Manage your assigned LLC formations</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by business name or client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            {filteredFormations.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">No LLC formations found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Business Name</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFormations.map((llc) => (
                    <TableRow key={llc.id}>
                      <TableCell className="font-medium">{llc.client_name}</TableCell>
                      <TableCell>{llc.business_name}</TableCell>
                      <TableCell>{llc.state}</TableCell>
                      <TableCell><Badge variant="outline">{llc.package}</Badge></TableCell>
                      <TableCell><StatusBadge status={llc.status} /></TableCell>
                      <TableCell>{new Date(llc.submitted_date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/agent/llc/${llc.id}`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
